import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Notifications,
  Settings,
  Logout,
  AccountCircle,
  KeyboardArrowDown,
  Brightness4,
  Brightness7,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useColorMode } from "../../../theme/ThemeContext";

const GOLD = "#D4AF37";

const Navbar = () => {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElNotify, setAnchorElNotify] = useState(null);
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const navigate = useNavigate();
  const { mode, toggleColorMode } = useColorMode();

  useEffect(() => {
    const storedFullName =
      localStorage.getItem("full_name") ||
      localStorage.getItem("email") ||
      "Admin User";
    const storedRole = localStorage.getItem("role") || "Admin";
    setFullName(storedFullName);
    setRole(storedRole);
  }, []);

  const notifications = [
    { id: 1, text: "New user registered", time: "2 min ago" },
    { id: 2, text: "Course approval pending", time: "1 hour ago" },
    { id: 3, text: "Payment received", time: "3 hours ago" },
  ];

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    const cookies = document.cookie.split(";");
    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    });
    window.location.href = "/db-shiksha/kaas/login";
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        boxShadow: "0 2px 15px rgba(0,0,0,0.05)",
        zIndex: 1200,
        width: "100%",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", minHeight: 64 }}>
        {/* Left side */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              opacity: 0,
              color: "text.primary",
              fontFamily: "'Outfit', sans-serif",
              letterSpacing: "-0.5px",
            }}
          >
            Admin{" "}
            <Box component="span" sx={{ color: GOLD }}>
              Portal
            </Box>
          </Typography>
        </Box>

        {/* Right side */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Tooltip title="Notifications">
            <IconButton
              onClick={(e) => setAnchorElNotify(e.currentTarget)}
              sx={{
                color: "text.secondary",
                "&:hover": { color: GOLD, bgcolor: alpha(GOLD, 0.05) },
              }}
            >
              <Badge badgeContent={notifications.length} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              mode === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"
            }
          >
            <IconButton
              onClick={toggleColorMode}
              sx={{
                color: mode === "dark" ? GOLD : "text.secondary",
                "&:hover": { color: GOLD, bgcolor: alpha(GOLD, 0.08) },
                transition: "all 0.3s ease",
              }}
            >
              {mode === "dark" ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Admin Settings">
            <IconButton
              onClick={() => navigate("/db-shiksha/kaas/admin/settings")}
              sx={{
                color: "text.secondary",
                "&:hover": { color: GOLD, bgcolor: alpha(GOLD, 0.05) },
              }}
            >
              <Settings />
            </IconButton>
          </Tooltip>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 1, height: 24, alignSelf: "center" }}
          />

          {/* User Profile */}
          <Box
            onClick={(e) => setAnchorElUser(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              p: 0.5,
              pr: 1.5,
              borderRadius: "50px",
              transition: "0.2s all",
              "&:hover": { bgcolor: alpha(GOLD, 0.05) },
            }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: GOLD,
                fontWeight: 700,
                fontSize: "0.9rem",
                border: `2px solid ${alpha(GOLD, 0.2)}`,
              }}
            >
              {fullName.charAt(0)}
            </Avatar>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
              >
                {fullName}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                {role}
              </Typography>
            </Box>
            <KeyboardArrowDown sx={{ color: "text.secondary", fontSize: 18 }} />
          </Box>

          {/* Notifications Menu */}
          <Menu
            anchorEl={anchorElNotify}
            open={Boolean(anchorElNotify)}
            onClose={() => setAnchorElNotify(null)}
            PaperProps={{
              sx: {
                width: 320,
                borderRadius: "16px",
                mt: 1.5,
                boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" fontWeight={800}>
                Notifications
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: GOLD, cursor: "pointer", fontWeight: 700 }}
              >
                Mark all read
              </Typography>
            </Box>
            <Divider />
            {notifications.map((n) => (
              <MenuItem
                key={n.id}
                onClick={() => setAnchorElNotify(null)}
                sx={{ py: 1.5, px: 2 }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {n.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {n.time}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
            <Divider />
            <MenuItem sx={{ justifyContent: "center", py: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: GOLD, fontWeight: 700 }}
              >
                View All Notifications
              </Typography>
            </MenuItem>
          </Menu>

          {/* User Menu */}
          <Menu
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={() => setAnchorElUser(null)}
            PaperProps={{
              sx: {
                width: 220,
                borderRadius: "16px",
                mt: 1.5,
                boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {fullName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {role}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorElUser(null);
                navigate("/db-shiksha/kaas/admin/profile");
              }}
            >
              <AccountCircle sx={{ fontSize: 20, mr: 1.5, color: GOLD }} />
              <Typography variant="body2" fontWeight={600}>
                My Profile
              </Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorElUser(null);
                navigate("/db-shiksha/kaas/admin/settings");
              }}
            >
              <Settings sx={{ fontSize: 20, mr: 1.5, color: GOLD }} />
              <Typography variant="body2" fontWeight={600}>
                Settings
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
              <Logout sx={{ fontSize: 20, mr: 1.5 }} />
              <Typography variant="body2" fontWeight={600}>
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
